import {
  Box,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import IconButton from "../../components/ui/IconButton";
import LoadingButton from "../../components/ui/LoadingButton";
import SearchField from "../../components/ui/SearchField";
import DeleteIcon from "@mui/icons-material/Delete";

import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import ExportVisitorsDialog from "../../components/ExportVisitorsDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import api from "../../services/api";
import lambda from "../../services/lambda";
import { useIntl } from "../../contexts/IntlContext";
import { PageHeader } from "../../components/PageHeader";
import { TableEmptyState } from "../../components/table/TableEmptyState";
import { TableSkeletonRows } from "../../components/table/TableSkeletonRows";
import { StandardTablePagination } from "../../components/table/StandardTablePagination";
import FiltersBar from "../../components/ui/FiltersBar";

interface Column {
  id:
    | "selected"
    | "name"
    | "phone"
    | "email"
    | "establishmentName"
    | "createdAt";
  label: string;
  minWidth?: number;
  align?: "right";
}



interface Data {
  selected: boolean;
  visitorId: string;
  name: string;
  phone: string;
  email: string;
  createdAt: string;
  establishmentName: string;
}

export function VisitorsList() {
  const intl = useIntl();
  const [visitors, setVisitors] = useState<Data[]>([]);

  const columns: readonly Column[] = [
    {
      id: "selected",
      label: "",
      minWidth: 10,
    },
    {
      id: "name",
      label: intl.formatMessage({ id: 'common.name' }),
      minWidth: 150,
    },
    { id: "phone", label: intl.formatMessage({ id: 'visitorsList.phone' }), minWidth: 150 },
    { id: "email", label: intl.formatMessage({ id: 'common.email' }), minWidth: 150 },
    { id: "establishmentName", label: intl.formatMessage({ id: 'visitorsList.establishment' }), minWidth: 150 },
    {
      id: "createdAt",
      label: intl.formatMessage({ id: 'visitorsList.createdAt' }),
      minWidth: 100,
    },
  ];

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<{ done: number; total: number } | null>(null);

  const [exportVisitorsModalOpen, setExportVisitorsModalOpen] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState("");
  const [exportSubmitting, setExportSubmitting] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [listLoading, setListLoading] = useState(false);

  const selectedVisitors = useMemo(
    () => visitors.filter((visitor) => visitor.selected),
    [visitors]
  );
  const allSelected = visitors.length > 0 && selectedVisitors.length === visitors.length;
  const someSelected = selectedVisitors.length > 0 && !allSelected;

  async function getVisitors() {
    setListLoading(true);
    try {
      const response = await api.get("app/visitors", {
        params: {
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          search: search.trim().length > 0 ? search : undefined,
        },
      });
      setVisitors(response.data.data);
      setCount(response.data.count);
    } catch (error: any) {
      if (
        error?.response?.status === 400 ||
        error?.response?.status === 401
      ) {
        toast.error(error.response.data.message);
      } else {
        toast.error(intl.formatMessage({ id: 'messages.connectionError' }));
      }
    } finally {
      setListLoading(false);
    }
  }

  useEffect(() => {
    getVisitors();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (search.trim().length > 0 && search.trim().length < 3) {
      return;
    }

    const timer = setTimeout(() => {
      getVisitors();
    }, 500);

    return () => {
      clearTimeout(timer);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleClick = (_event: React.MouseEvent<unknown>, id: string) => {
    setVisitors((prev) =>
      prev.map((visitor) =>
        visitor.visitorId === id
          ? { ...visitor, selected: !visitor.selected }
          : visitor
      )
    );
  };

  const handleToggleSelectAll = (checked: boolean) => {
    setVisitors((prev) => prev.map((visitor) => ({ ...visitor, selected: checked })));
  };

  async function handleDeleteVisitors() {
    const targets = selectedVisitors;
    if (targets.length === 0 || deleteProgress) return;
    try {
      setDeleteProgress({ done: 0, total: targets.length });
      const deletedIds = new Set<string>();
      for (const visitor of targets) {
        await api.delete(`app/visitors/${visitor.visitorId}`);
        deletedIds.add(visitor.visitorId);
        setDeleteProgress({ done: deletedIds.size, total: targets.length });
      }

      setVisitors((prev) => prev.filter((visitor) => !deletedIds.has(visitor.visitorId)));
      setDeleteModalOpen(false);
      toast.success(intl.formatMessage({ id: 'visitorsList.deleteSuccess' }));
    } catch (error) {
      // @ts-ignore
      if (error?.response?.status === 400 || error?.response?.status === 401) {
        // @ts-ignore
        toast.error(error.response.data.message);
      } else {
        toast.error(intl.formatMessage({ id: 'messages.connectionError' }));
      }
    } finally {
      setDeleteProgress(null);
    }
  }

  async function handleExportVisitors() {
    setExportSubmitting(true);
    try {
      await lambda.post(`/contacts/export`, { receiptEmail });

      setReceiptEmail("");
      setExportVisitorsModalOpen(false);

      toast.success(intl.formatMessage({ id: 'visitorsList.exportSuccess' }));
    } catch (error) {
      // @ts-ignore
      if (error?.response?.status === 400 || error?.response?.status === 401) {
        // @ts-ignore
        toast.error(error.response.data.message);
      } else {
        toast.error(intl.formatMessage({ id: 'messages.connectionError' }));
      }
    } finally {
      setExportSubmitting(false);
    }
  }

  return (
    <>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <PageHeader
          title={intl.formatMessage({ id: 'visitorsList.title' })}
          selectedLabel={intl.formatMessage(
            { id: 'visitorsList.selectedCount' },
            { count: selectedVisitors.length }
          )}
          isSelectionActive={selectedVisitors.length > 0}
          loading={listLoading}
          sx={{ px: 2, py: 1 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Tooltip title={intl.formatMessage({ id: 'visitorsList.exportAllHint' })}>
              <LoadingButton
                onClick={() => setExportVisitorsModalOpen(true)}
                size="small"
                color="primary"
                variant="contained"
                sx={{ width: { xs: "100%", sm: "150px" } }}
              >
                {intl.formatMessage({ id: 'common.export' })}
              </LoadingButton>
            </Tooltip>

            <span>
              <IconButton
                tooltip={intl.formatMessage({ id: 'common.delete' })}
                onClick={() => setDeleteModalOpen(true)}
                disabled={selectedVisitors.length === 0}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Box>
        </PageHeader>

        <Box sx={{ px: 2, pb: 2 }}>
          <FiltersBar activeFilterCount={search ? 1 : 0} sticky={false}>
            <Tooltip title={intl.formatMessage({ id: 'visitorsList.searchMinChars' })}>
              <SearchField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={intl.formatMessage({ id: 'visitorsList.searchPlaceholder' })}
                sx={{ width: { xs: "100%", sm: "220px" }, marginY: 0 }}
              />
            </Tooltip>
          </FiltersBar>
        </Box>

        <TableContainer sx={{ maxHeight: '65vh' }}>
          <Table stickyHeader size="small" aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) =>
                  column.id === "selected" ? (
                    <TableCell key={column.id} padding="checkbox">
                      <Checkbox
                        color="primary"
                        indeterminate={someSelected}
                        checked={allSelected}
                        disabled={visitors.length === 0}
                        onChange={(e) => handleToggleSelectAll(e.target.checked)}
                        inputProps={{ "aria-label": intl.formatMessage({ id: "visitorsList.selectAll" }) }}
                      />
                    </TableCell>
                  ) : (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {listLoading ? (
                <TableSkeletonRows
                  rows={rowsPerPage > 10 ? 10 : rowsPerPage}
                  columns={columns.length}
                />
              ) : visitors.length === 0 ? (
                <TableEmptyState colSpan={columns.length}>
                  {intl.formatMessage({ id: "visitorsList.empty" })}
                </TableEmptyState>
              ) : (
                visitors.map((row) => {
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      selected={row.selected}
                      aria-checked={row.selected}
                      onClick={(event) => handleClick(event, row.visitorId)}
                      sx={{ cursor: "pointer" }}
                      key={row.visitorId}
                    >
                      {columns.map((column) => {
                        const value = row[column.id];

                        if (column.id === "selected") {
                          return (
                            <TableCell padding="checkbox" key={column.id}>
                              <Checkbox
                                color="primary"
                                checked={row.selected}
                                inputProps={{
                                  "aria-labelledby": row.visitorId,
                                }}
                              />
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell key={column.id} align={column.align}>
                            {value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <StandardTablePagination
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <ConfirmDialog
        open={deleteModalOpen}
        title={intl.formatMessage({ id: "common.confirm" })}
        message={
          deleteProgress
            ? intl.formatMessage(
                { id: "visitorsList.deleteProgress" },
                { done: deleteProgress.done, total: deleteProgress.total }
              )
            : selectedVisitors.length > 1
              ? intl.formatMessage(
                  { id: "visitorsList.deleteManyConfirm" },
                  { count: selectedVisitors.length }
                )
              : intl.formatMessage({ id: "deleteDialog.confirmMessage" })
        }
        confirmLabel={intl.formatMessage({ id: "common.yes" })}
        cancelLabel={intl.formatMessage({ id: "common.no" })}
        onConfirm={handleDeleteVisitors}
        onClose={() => setDeleteModalOpen(false)}
      />

      <ExportVisitorsDialog
        open={exportVisitorsModalOpen}
        setOpen={setExportVisitorsModalOpen}
        handle={handleExportVisitors}
        receiptEmail={receiptEmail}
        setReceiptEmail={setReceiptEmail}
        submitting={exportSubmitting}
      />
    </>
  );
}
